import {
  Button,
  ButtonState,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { PasswordInput } from '@delmonicos/shared-design-system-react/src/components/input/password-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import {
  DIGIT_PATTERN,
  LOWERCASE_PATTERN,
  SPECIAL_CHARACTER_PATTERN,
  UPPERCASE_PATTERN,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/patterns';
import {
  UpdatePasswordFormModel,
  useUpdatePasswordFormValidation,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/inputs/users/update-password.form-validation';
import { UpdatePasswordModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/inputs/users/update-password.model';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RuleComponent } from './rule.component';

function mapUpdatePasswordFormModelToUpdatePasswordModel(
  payload: Omit<UpdatePasswordModel, 'id'>,
): Omit<UpdatePasswordModel, 'id'> {
  return {
    newPassword: payload.newPassword,
    currentPassword: payload.currentPassword,
  };
}

type UpdatePasswordFormProps = {
  readonly title?: string;
  readonly caption?: string;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onSubmit: (payload: Omit<UpdatePasswordModel, 'id'>) => void;
};

export function UpdatePasswordForm(
  props: UpdatePasswordFormProps,
): React.JSX.Element {
  const { Header } = Dialog;
  const { t } = useTranslation();
  const {
    isLoading,
    isError,
    onSubmit,
    title = t('$.update-password.title'),
    caption = t('$.update-password.caption'),
  } = props;

  const formValidation = useUpdatePasswordFormValidation();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<UpdatePasswordFormModel>({
    mode: 'onTouched',
  });

  const newPassword = watch('newPassword');

  return (
    <>
      <Header title={title} />
      <form
        noValidate
        onSubmit={handleSubmit((payload) =>
          onSubmit(mapUpdatePasswordFormModelToUpdatePasswordModel(payload)),
        )}>
        <Stack
          justifyContent="space-between"
          flex={1}
          direction="column"
          gap={GapEnum.XL}>
          <Stack direction="column" gap={GapEnum.M} alignItems="stretch">
            <Text>{caption}</Text>
            <PasswordInput
              {...register('currentPassword', formValidation.currentPassword)}
              autoFocus
              errorMessage={errors?.currentPassword?.message}
              id="current-password"
              label={t(
                'delmonicos-shared-sdk.inputs-update-password.current-password',
              )}
            />
            <Stack direction="column" gap={GapEnum.XS}>
              <PasswordInput
                {...register('newPassword', formValidation.newPassword)}
                autoComplete="new-password"
                id="new-password"
                label={t(
                  'delmonicos-shared-sdk.inputs-update-password.new-password',
                )}
                errorMessage={errors?.newPassword?.message}
              />
              <Stack direction="column" gap={GapEnum.XXS}>
                <RuleComponent
                  title={t('$.update-password.rules.length')}
                  isValid={newPassword?.length >= 16}
                />
                <RuleComponent
                  title={t('$.update-password.rules.digit')}
                  isValid={DIGIT_PATTERN.test(newPassword || '')}
                />
                <RuleComponent
                  title={t('$.update-password.rules.uppercase')}
                  isValid={UPPERCASE_PATTERN.test(newPassword || '')}
                />
                <RuleComponent
                  title={t('$.update-password.rules.lowercase')}
                  isValid={LOWERCASE_PATTERN.test(newPassword || '')}
                />
                <RuleComponent
                  title={t('$.update-password.rules.special')}
                  isValid={SPECIAL_CHARACTER_PATTERN.test(newPassword || '')}
                />
              </Stack>
            </Stack>
            <PasswordInput
              {...register(
                'passwordVerification',
                formValidation.passwordVerification,
              )}
              autoComplete="off"
              id="password-verification"
              label={t(
                'delmonicos-shared-sdk.inputs-update-password.confirm-password',
              )}
              errorMessage={errors?.passwordVerification?.message}
            />
            {isError && (
              <div>
                <Text color={TextColor.Error}>
                  {t('$.update-password.error')}
                </Text>
              </div>
            )}
          </Stack>
          <Button
            type="submit"
            state={isLoading ? ButtonState.Loading : ButtonState.Default}
            disabled={!isValid}>
            {t('$.update-password.submit-button')}
          </Button>
        </Stack>
      </form>
    </>
  );
}
